import { Box } from "@mui/material";
import { setInnCode } from "components/app/App.slice";
import BookmarkPlan from "components/common/Bookmark";
import { copyAvailableFiltersToPage } from "components/common/Filters/FiltersFunctions";
import { filterPage } from "components/common/Filters/FiltersHelper";
import { setPageFilter } from "components/common/Filters/GlobalFilters.slice";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { setProgressInfoSummary } from "components/features/Progress/Progress.slice";
import { useTranslate } from "hooks/useTranslate";
import { Plan } from "Interface/plan";
import { uniq, uniqBy } from "lodash";
import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, DEFAULT_CORP_INNCODE, corpViewTypes } from "utils/CommonConstants";
import {
  decrypt,
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getKeyMetricsPlansData,
  getKeyMetricsPlansDataSC,
  getMoreValues,
  getSourceName,
  getTableColor,
  isBookmarked
} from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { fetchCorpDashboardPlansAsync, fetchCorpDashboardPlansOverviewAsync, reset } from "../../Corp/CorpUser.Slice";
import { getAllBreakupLists, getmyListType } from "../MyLists";
import "./ListDataView.scss";
import { tabs, useFilterRequestPayload } from "./ListDataViewHelper";
import ListKeyMetrics from "./ListKeyMetrics";
import TableEdit from "./TableEdit";
import {
  dashboardPlansHeaders,
  dashboardTemplatePlansHeaders,
  plansHeaders,
  plansHeadersSC,
  templatePlansHeaders
} from "./TableHeadersConfig";

const PlansData = ({ view, selectedListType, selectedList }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const { list: plansList, overview } = useAppSelector((state) => state.CorpDashboard.myView.plans);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const listCategory = useAppSelector((state) => state.CorpDashboard.myListsSelected.listCategory);
  const lobbyUserType = decrypt("salt", localStorage.getItem("lobbyUserType"));
  const searchedText = useAppSelector((state) => state.CorpDashboard.searchText?.[view]);

  const tableName = "plansSort_" + view;
  const sorting = useAppSelector((state) => state.sortingDetails.sorting);
  const recentSortingApplied = sorting?.[tableName];

  const { dynamicSelectFlag, filterRequestPayload, formattedFilterRequestPayload } = useFilterRequestPayload(
    view,
    selectedListType
  );

  const myListType = getmyListType(selectedListType, selectedList?.name);

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);

  const fetchSummaryAndOverview = (searchText: string = searchedText, callOverview: boolean = true) => {
    let payloadData: any = {
      ...formattedFilterRequestPayload,
      ...dynamicSelectFlag,
      searchText,
      view: view,
      fetchTemplatePlans: tab === tabs.template_plans,
      sort: { ...recentSortingApplied }
    };
    payloadData =
      view === corpViewTypes.myList
        ? { ...payloadData, myListType: myListType, myListId: selectedList?.id }
        : payloadData;
    dispatch(fetchCorpDashboardPlansAsync({ ...payloadData }));
    if (callOverview)
      dispatch(
        fetchCorpDashboardPlansOverviewAsync({
          ...payloadData
        })
      );
  };

  const isMountedRef = useRef(false);
  const prevFilterRef = useRef(filterRequestPayload);
  useEffect(() => {
    if (String(lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE) return;

    const filterChanged = !isMountedRef.current || prevFilterRef.current !== filterRequestPayload;
    isMountedRef.current = true;
    prevFilterRef.current = filterRequestPayload;

    fetchSummaryAndOverview(searchedText, filterChanged);
  }, [filterRequestPayload, recentSortingApplied]);

  const getInputDetail = (val: string) => {
    fetchSummaryAndOverview(val, false);
  };

  const onPlanClick = (plan: Plan) => {
    if (tab === tabs.template_plans && view === "dashboard") {
      dispatch(
        setPageFilter({
          pageName: filterPage.templatePlanProgressDisplay,
          modifiedFilter: filterRequestPayload?.filter((item: any) =>
            ["brandCode", "region", "planStatus", "mgmtType"].includes(item?.key)
          )
        })
      );
    }
    if (tab === tabs.template_plans && selectedListType === "default") {
      navigate(`/${routes.TemplatePlanDetail}/${plan?.templatePlanId}?parentTab=Plan+Progress`, {
        state: {
          isMyLists: true,
          redirectFromDashboard: true
        }
      });
      return;
    }
    if (selectedListType === "consultant") {
      const link = `/progressInfo/plan/${selectedList?.id}?${
        tab === tabs.template_plans ? "templatePlanId" : "cmsId"
      }=${plan?.planCmsId || plan?.templatePlanId}`;
      navigate(link, {
        state: {
          isMyLists: true,
          defaultTab: 1,
          innCodes: plan?.implementedInncodes
        }
      });
      dispatch(setProgressInfoSummary(plan));
      return;
    }

    copyAvailableFiltersToPage(dispatch, filterPage.planProgressFromLibraryDisplay, filterRequestPayload);
    dispatch(setInnCode(DEFAULT_CORP_INNCODE));
    navigate(`/plandetails/${plan?.planCmsId}`, {
      state: {
        isMyLists: true,
        defaultTab: 1,
        innCodes: plan?.implementedInncodes,
        redirectFromDashboard: true
      }
    });
  };

  const getRowData = (plansList: any) => {
    return plansList?.map((plan: any) => {
      return {
        data: getChildObject(plan, plansList)
      };
    });
  };

  const getChildObject = (plan: any, plansList: any) => {
    let data: any = [
      {
        content: (
          <Box
            onClick={() => onPlanClick(plan)}
            sx={{
              textDecoration: "none",
              cursor: "pointer",
              color: tokens.Text.General.PrimaryHeadline,
              fontWeight: "700"
            }}
          >
            {plan?.planName || plan?.myPlanName}
          </Box>
        ),
        showBold: true
      },
      {
        content: getMoreValues(functionalAreaIdsToNames(plan.functionalAreas)),
        tooltip: {
          title: "Functional Areas",
          list: plan?.functionalAreas?.map((FA: any) => {
            return { name: functionalAreaIdToName(FA, functionalAreaList) };
          })
        }
      },
      ...(tab !== tabs.template_plans
        ? [
            {
              content: getMoreValues(uniq(plan?.planSources?.map((source: any) => getSourceName(source)))),
              tooltip: {
                list: uniqBy(
                  plan?.planSources?.map((src: any) => {
                    return { name: getSourceName(src) };
                  }),
                  "name"
                )
              }
            }
          ]
        : []),

      {
        content: listCategory === "consultant" && view === "myList" ? plan.plansCount : plan.implementedPropertiesCount
      }
    ];
    if (view === "myList") {
      data.push({
        content: geStatusValues(plan?.implementedPlansCountStatusBreakUp)
      });
      if (tab !== tabs.template_plans)
        data.push({
          content: (
            <BookmarkPlan
              innCodeForPayload={DEFAULT_CORP_INNCODE}
              cmsId={plan?.planCmsId}
              isBookmarked={isBookmarked(plan?.planCmsId, DEFAULT_CORP_INNCODE)}
              onClickFunction={() => {
                if (selectedList?.name === "Bookmarked Plans") {
                  fetchSummaryAndOverview();
                }
              }}
            />
          )
        });
    }

    return data;
  };

  const geStatusValues = (statusBreakUp: any) => {
    const status = getAllBreakupLists(statusBreakUp);

    return (
      <>
        {status?.map((item: any) => {
          return (
            <div style={{ color: getTableColor(item?.name), whiteSpace: "nowrap" }}>
              <Typography component={"span"} variant={"body2"} showBold>
                {item.value < 10 ? "0" : ""}
                {`${item.value ?? "00"}  `}
              </Typography>
              <Typography component={"span"}>
                {item?.name} {item?.isShowStar > 0 ? "*" : ""}
              </Typography>
            </div>
          );
        })}
      </>
    );
  };

  const getHeaders = () => {
    if (view === "dashboard") {
      if (tab === tabs.template_plans) return dashboardTemplatePlansHeaders;
      return dashboardPlansHeaders;
    }
    if (listCategory === "consultant") {
      if (tab === tabs.template_plans) return templatePlansHeaders;
      return plansHeadersSC;
    }
    return plansHeaders;
  };

  return (
    <div className="corp-view-tab-data-component">
      <ListKeyMetrics
        view={view}
        listCategory={listCategory}
        keyMetricsData={
          listCategory === "consultant" && view === "myList"
            ? getKeyMetricsPlansDataSC(overview)
            : getKeyMetricsPlansData(overview)
        }
      />
      <TableEdit
        view={view}
        selectedListType={selectedListType}
        getInputDetails={(val: string) => getInputDetail(val)}
      />
      <BasicTable
        noDataMessage={translate("common.noResults")}
        data={{
          headers: getHeaders(),
          rows: getRowData(plansList)
        }}
        tableName={tableName}
      />
    </div>
  );
};
export default React.memo(PlansData);
